import {
  type ContractInvoicingDto,
  datePl,
  formatMoney,
  INVOICE_KINDS,
  INVOICE_STATUSES,
  KSEF_STATUS_LABELS,
  type MeDto,
  SALES_DOC_FILTERS,
  SALES_PAY_FILTERS,
  type SalesDocFilter,
  type SalesInvoiceDetailDto,
  type SalesInvoiceRelatedDto,
  type SalesInvoiceRowDto,
  type SalesPayFilter,
  type SalesRegisterDto,
  clampDateTo,
} from '@bqcrm/shared';
import { Anchor, Badge, Button, Card, Drawer, Group, Pagination, Select, SimpleGrid, Stack, Table, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconCash, IconDownload, IconFileTypePdf, IconSearch, IconSend } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { CompanyBadgeById, InvoicePayStatusBadge, TestModeBadge } from '../../components/Badges';
import { InvoicePaymentModal } from '../../components/InvoicePayment';
import { InvoicesPanel } from '../../components/InvoicesPanel';
import { RelatedInvoiceDocs } from '../../components/RelatedInvoiceDocs';
import { Money } from '../../components/Money';
import { QueryState } from '../../components/QueryState';
import { EmptyState, SectionCard, FilterChips } from '../../components/ui';
import { notifyError, notifyOk } from '../../notify';
import { downloadCsv } from './csv';

const PAGE = 50;

/** Krótka nazwa rodzaju w tabeli. */
const KIND_SHORT: Record<string, string> = { zamowienie: 'Zamówienie', proforma: 'Pro forma', zaliczkowa: 'Zaliczkowa', koncowa: 'Końcowa', vat: 'VAT', korekta: 'Korekta' };

function PayState({ r }: { r: SalesInvoiceRowDto }) {
  if (r.status !== 'wystawiona')
    return (
      <Badge size="sm" variant="light" color={r.status === 'blad' || r.status === 'niepewna' ? 'red' : 'gray'}>
        {INVOICE_STATUSES[r.status]}
      </Badge>
    );
  if (r.kind === 'proforma' && r.convertedTo)
    return (
      <Text size="xs" c="dimmed">
        zamieniona na {r.convertedTo}
      </Text>
    );
  if (r.kind === 'proforma' || r.kind === 'zamowienie') return <Text size="xs" c="dimmed">nie dotyczy</Text>;
  if (r.kind === 'zaliczkowa')
    return (
      <Badge size="sm" variant="light" color="green">
        Opłacona (z wpłaty)
      </Badge>
    );
  if (r.kind === 'korekta') return <Text size="xs" c="dimmed">korekta</Text>;
  return <InvoicePayStatusBadge extStatus={r.toPayGr === 0 ? 'paid' : r.extStatus} overdue={r.overdue} />;
}

/** Finanse → Faktury sprzedaży: rejestr dokumentów wystawionych z CRM przez Fakturownię. */
export function SalesRegister({ me, companyId }: { me: MeDto; companyId: string | null }) {
  const [text, setText] = useState('');
  const [search] = useDebouncedValue(text.trim(), 300);
  const [docs, setDocs] = useState<SalesDocFilter>('ksiegowe');
  const [pay, setPay] = useState<SalesPayFilter>('wszystkie');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<SalesInvoiceRowDto | null>(null);

  const params = (offset: number, limit: number) => {
    const p = new URLSearchParams({ docs, pay, limit: String(limit), offset: String(offset) });
    if (search) p.set('q', search);
    if (companyId) p.set('companyId', companyId);
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    return p;
  };
  const key = params((page - 1) * PAGE, PAGE).toString();
  const q = useQuery({ queryKey: ['sales-register', key], queryFn: () => api<SalesRegisterDto>(`/finance/sales-invoices?${key}`), placeholderData: (prev) => prev });
  const resetPage = <T,>(fn: (v: T) => void) => (v: T) => {
    fn(v);
    setPage(1);
  };

  const exportCsv = async () => {
    try {
      const rows: SalesInvoiceRowDto[] = [];
      for (let offset = 0; offset < 10_000; offset += 200) {
        const part = await api<SalesRegisterDto>(`/finance/sales-invoices?${params(offset, 200)}`);
        rows.push(...part.items);
        if (rows.length >= part.total || !part.items.length) break;
      }
      downloadCsv(
        `faktury-sprzedazy${from ? `-od-${from}` : ''}${to ? `-do-${to}` : ''}.csv`,
        ['Numer', 'Rodzaj', 'Data wystawienia', 'Data sprzedaży', 'Nabywca', 'NIP', 'Netto', 'VAT', 'Brutto', 'Zapłacono', 'Do zapłaty', 'Termin', 'Numer KSeF', 'Sprawa', 'Umowa', 'Zgłoszenie serwisowe'],
        rows.map((r) => [r.number ?? '', KIND_SHORT[r.kind] ?? r.kind, r.issueDate ?? '', r.sellDate ?? '', r.buyerName, r.buyerNip, r.netGr, r.vatGr, r.grossGr, r.paidGr, r.toPayGr, r.paymentTo ?? '', r.ksefNumber ?? '', r.source === 'umowa' ? r.caseNumber : '', r.contractNumber ?? '', r.source === 'serwis' ? r.caseNumber : '']),
      );
    } catch (e) {
      notifyError(e);
    }
  };

  const s = q.data?.sums;
  return (
    <Stack gap="md">
      <Group gap="sm" align="flex-end" wrap="wrap">
        <TextInput
          label="Szukaj"
          placeholder="Numer faktury, numer KSeF, nabywca, NIP, sprawa"
          leftSection={<IconSearch size={16} />}
          value={text}
          onChange={(e) => {
            setText(e.currentTarget.value);
            setPage(1);
          }}
          w={{ base: '100%', sm: 360 }}
        />
        <Select label="Dokumenty" data={Object.entries(SALES_DOC_FILTERS).map(([value, label]) => ({ value, label }))} value={docs} onChange={resetPage((v: string | null) => setDocs((v as SalesDocFilter) ?? 'ksiegowe'))} allowDeselect={false} w={{ base: '100%', sm: 300 }} />
        <TextInput label="Wystawione od" type="date" value={from} onChange={(e) => { const v = e.currentTarget.value; resetPage(setFrom)(v); setTo(clampDateTo(v, to)); }} w={{ base: '48%', sm: 160 }} />
        <TextInput label="do" type="date" value={to} min={from || undefined} onChange={(e) => resetPage(setTo)(e.currentTarget.value)} w={{ base: '48%', sm: 160 }} />
        <Button variant="default" leftSection={<IconDownload size={16} />} onClick={() => void exportCsv()} disabled={!q.data?.total}>
          Eksport do CSV
        </Button>
      </Group>
      <FilterChips value={pay} onChange={resetPage((v: SalesPayFilter) => setPay(v))} options={(Object.keys(SALES_PAY_FILTERS) as SalesPayFilter[]).map((value) => ({ value, label: SALES_PAY_FILTERS[value] }))} />

      {s && (
        <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="sm">
          {[
            ['Netto', s.netGr, false],
            ['VAT', s.vatGr, false],
            ['Brutto', s.grossGr, false],
            ['Do zapłaty', s.toPayGr, false],
            ['Po terminie', s.overdueGr, s.overdueGr > 0],
          ].map(([label, v, alert]) => (
            <Card key={label as string} padding="sm" withBorder style={alert ? { borderColor: 'var(--mantine-color-red-4)' } : undefined}>
              <Text size="xs" c="dimmed">
                {label as string} <Text span size="xs">({q.data!.total} dok.)</Text>
              </Text>
              <Text fw={700} className="tabular" c={alert ? 'var(--app-text-red)' : undefined}>
                <Money gr={v as number} />
              </Text>
            </Card>
          ))}
        </SimpleGrid>
      )}

      <Card padding={0}>
        <QueryState isLoading={q.isLoading} error={q.error}>
          <Table.ScrollContainer minWidth={1100}>
            <Table highlightOnHover verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Numer</Table.Th>
                  <Table.Th>Wystawiona</Table.Th>
                  <Table.Th>Nabywca</Table.Th>
                  <Table.Th ta="right">Netto</Table.Th>
                  <Table.Th ta="right">VAT</Table.Th>
                  <Table.Th ta="right">Brutto</Table.Th>
                  <Table.Th ta="right">Do zapłaty</Table.Th>
                  <Table.Th>Termin</Table.Th>
                  <Table.Th>Rozliczenie</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {q.data?.items.map((r) => (
                  <Table.Tr key={r.id} className="row-link" onClick={() => setOpen(r)} style={r.overdue ? { backgroundColor: 'var(--mantine-color-red-light)' } : undefined}>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <CompanyBadgeById id={r.companyId} />
                        <Text size="sm" fw={600} style={{ whiteSpace: 'nowrap' }}>
                          {r.number ?? '(bez numeru)'}
                        </Text>
                        {r.mode === 'test' && <TestModeBadge />}
                      </Group>
                      <Text size="xs" c="dimmed">
                        {KIND_SHORT[r.kind] ?? r.kind}
                        {r.source === 'serwis' ? ' · serwis' : ''}
                        {r.parentNumber && r.kind === 'korekta' ? ` do ${r.parentNumber}` : ''}
                        {r.ksefStatus ? ` · KSeF: ${KSEF_STATUS_LABELS[r.ksefStatus] ?? r.ksefStatus}` : ''}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{r.issueDate ? datePl(r.issueDate) : '—'}</Text>
                      {r.sellDate && r.sellDate !== r.issueDate && (
                        <Text size="xs" c="dimmed">
                          sprzedaż {datePl(r.sellDate)}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" lineClamp={1}>
                        {r.buyerName || '—'}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {r.buyerNip ? `NIP ${r.buyerNip} · ` : ''}
                        {r.source === 'serwis' ? `zgłoszenie ${r.caseNumber}` : r.caseNumber}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Money gr={r.netGr} />
                    </Table.Td>
                    <Table.Td ta="right">
                      <Money gr={r.vatGr} />
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm" fw={600}>
                        <Money gr={r.grossGr} />
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">{r.toPayGr === null ? <Text size="sm" c="dimmed">—</Text> : <Money gr={r.toPayGr} />}</Table.Td>
                    <Table.Td>
                      <Text size="sm" c={r.overdue ? 'var(--app-text-red)' : undefined} fw={r.overdue ? 600 : 400}>
                        {r.paymentTo ? datePl(r.paymentTo) : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <PayState r={r} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {!q.data?.items.length && <EmptyState compact title={search ? 'Nie znaleziono faktur' : 'Brak faktur'} hint={search ? 'Sprawdź numer albo zmień filtry.' : 'Faktury wystawione z umów i za serwis pojawiają się tu automatycznie.'} />}
          {(q.data?.total ?? 0) > PAGE && (
            <Group justify="center" p="sm">
              <Pagination total={Math.ceil(q.data!.total / PAGE)} value={page} onChange={setPage} size="sm" />
            </Group>
          )}
        </QueryState>
      </Card>
      <SalesInvoiceDrawer row={open} onClose={() => setOpen(null)} canConfigure={can(me, 'integracje.konfiguracja')} />
    </Stack>
  );
}

/** Szczegóły faktury sprzedaży: dane, pozycje, działania; poniżej wszystkie dokumenty umowy (korekta, ponowienie…). */
function SalesInvoiceDrawer({ row, onClose, canConfigure }: { row: SalesInvoiceRowDto | null; onClose: () => void; canConfigure: boolean }) {
  const qc = useQueryClient();
  const key = ['contract-invoicing', row?.contractId];
  // umowa: wpłata do faktury i wszystkie dokumenty umowy; szczegóły faktury — wspólne dla każdego źródła
  const st = useQuery({ queryKey: key, queryFn: () => api<ContractInvoicingDto>(`/contracts/${row!.contractId}/invoicing`), enabled: !!row?.contractId });
  const detailKey = ['sales-invoice', row?.id];
  const det = useQuery({ queryKey: detailKey, queryFn: () => api<SalesInvoiceDetailDto>(`/finance/sales-invoices/${row!.id}`), enabled: !!row });
  // źródło bez własnego panelu (serwis): „Wszystkie dokumenty tego zgłoszenia” i bieżący stan faktury po odświeżeniu
  const relKey = ['sales-invoice-related', row?.id];
  const rel = useQuery({ queryKey: relKey, queryFn: () => api<SalesInvoiceRelatedDto | null>(`/finance/sales-invoices/${row!.id}/related`), enabled: !!row && !row.contractId });
  const [paying, setPaying] = useState(false);
  const inv = st.data?.invoices.find((i) => i.id === row?.id);
  const d = det.data;
  const r = (row && rel.data?.documents.find((x) => x.id === row.id)) || row;
  const refreshAll = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: detailKey });
    void qc.invalidateQueries({ queryKey: relKey });
    void qc.invalidateQueries({ queryKey: ['sales-register'] });
  };
  const send = useMutation({
    mutationFn: () => api<SalesInvoiceDetailDto>(`/finance/sales-invoices/${row!.id}/send`, { method: 'POST' }),
    onSuccess: (s) => {
      qc.setQueryData(detailKey, s);
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: relKey });
      notifyOk(s.emailTo ? `Wysłano fakturę do klienta (${s.emailTo})` : 'Wysłano fakturę do klienta');
    },
    onError: notifyError,
  });
  const refresh = useMutation({
    mutationFn: () => api<SalesInvoiceDetailDto>(`/finance/sales-invoices/${row!.id}/refresh`, { method: 'POST' }),
    onSuccess: (s) => (qc.setQueryData(detailKey, s), refreshAll(), notifyOk('Pobrano stan z Fakturowni')),
    onError: notifyError,
  });

  return (
    <Drawer opened={!!row} onClose={onClose} position="right" size="xl" title={row ? `${INVOICE_KINDS[row.kind]} ${row.number ?? ''}`.trim() : ''}>
      {row && r && (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
            <Text size="sm">
              <Text span c="dimmed">Nabywca: </Text>
              {r.buyerName}
              {r.buyerNip ? ` (NIP ${r.buyerNip})` : ''}
            </Text>
            <Text size="sm">
              <Text span c="dimmed">Wystawiona / sprzedaż: </Text>
              {r.issueDate ? datePl(r.issueDate) : '—'} / {r.sellDate ? datePl(r.sellDate) : '—'}
            </Text>
            <Text size="sm">
              <Text span c="dimmed">Netto / VAT / brutto: </Text>
              {formatMoney(r.netGr ?? 0)} / {formatMoney(r.vatGr ?? 0)} / <b>{formatMoney(r.grossGr ?? 0)}</b>
            </Text>
            <Text size="sm">
              <Text span c="dimmed">Termin płatności: </Text>
              {r.paymentTo ? datePl(r.paymentTo) : '—'}
              {r.toPayGr !== null && <> · do zapłaty <b>{formatMoney(r.toPayGr)}</b></>}
            </Text>
            <Text size="sm">
              <Text span c="dimmed">KSeF: </Text>
              {r.ksefNumber ?? (r.ksefStatus ? (KSEF_STATUS_LABELS[r.ksefStatus] ?? r.ksefStatus) : '—')}
            </Text>
            {r.source === 'serwis' ? (
              <Text size="sm">
                <Text span c="dimmed">Zgłoszenie serwisowe: </Text>
                <Anchor component={Link} to={`/serwis?zgloszenie=${r.ticketId}`} size="sm">
                  {r.caseNumber}
                </Anchor>
              </Text>
            ) : (
              <Text size="sm">
                <Text span c="dimmed">Sprawa / umowa: </Text>
                <Anchor component={Link} to={`/sprawy/${r.caseId}?zakladka=platnosci`} size="sm">
                  {r.caseNumber}
                </Anchor>
                {' · '}
                <Anchor component={Link} to={`/umowy/${r.contractId}`} size="sm">
                  {r.contractNumber ?? 'umowa'}
                </Anchor>
              </Text>
            )}
          </SimpleGrid>
          <Group gap="xs">
            {d?.can.pdf && (
              <Button size="xs" variant="light" component="a" href={`/api/finance/sales-invoices/${row.id}/pdf`} target="_blank" rel="noreferrer" leftSection={<IconFileTypePdf size={14} />}>
                Pobierz PDF
              </Button>
            )}
            {inv && d?.can.registerPayment && (
              <Button size="xs" leftSection={<IconCash size={14} />} onClick={() => setPaying(true)}>
                Zarejestruj wpłatę
              </Button>
            )}
            {d?.can.send && (
              <Button size="xs" variant="light" leftSection={<IconSend size={14} />} loading={send.isPending} onClick={() => send.mutate()}>
                Wyślij do klienta
              </Button>
            )}
            {d?.can.refresh && (
              <Button size="xs" variant="subtle" loading={refresh.isPending} onClick={() => refresh.mutate()}>
                Odśwież z Fakturowni
              </Button>
            )}
          </Group>

          {d && d.items.length > 0 && (
            <SectionCard title="Pozycje" padding={0}>
              <Table.ScrollContainer minWidth={560}>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Nazwa</Table.Th>
                      <Table.Th ta="right">Ilość</Table.Th>
                      <Table.Th ta="right">VAT</Table.Th>
                      <Table.Th ta="right">Netto</Table.Th>
                      <Table.Th ta="right">Brutto</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {d.items.map((it, i) => (
                      <Table.Tr key={i}>
                        <Table.Td>{it.name}</Table.Td>
                        <Table.Td ta="right">
                          {it.quantity} {it.unit}
                        </Table.Td>
                        <Table.Td ta="right">{it.vatRate}</Table.Td>
                        <Table.Td ta="right">
                          <Money gr={it.netGr} />
                        </Table.Td>
                        <Table.Td ta="right">
                          <Money gr={it.grossGr} />
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </SectionCard>
          )}

          {rel.data && <RelatedInvoiceDocs data={rel.data} onChanged={refreshAll} />}
          {row.contractId && (
            <>
              <Text fw={700} size="sm">
                Wszystkie dokumenty tej umowy
              </Text>
              <InvoicesPanel contractId={row.contractId} canConfigure={canConfigure} />
            </>
          )}
          {inv && row.contractId && (
            <InvoicePaymentModal
              invoiceId={inv.id}
              number={inv.number}
              contractId={row.contractId}
              toPayGr={inv.toPayGr}
              opened={paying}
              onClose={() => setPaying(false)}
              onDone={() => {
                setPaying(false);
                refreshAll();
              }}
            />
          )}
        </Stack>
      )}
    </Drawer>
  );
}
